import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://study-bangladesh-api.a4aerolimited711.workers.dev';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  register: (data: { name: string; phone?: string; email?: string; password: string; classId: number }) =>
    api.post('/api/auth/register', data),
  
  login: (data: { phone?: string; email?: string; password: string }) =>
    api.post('/api/auth/login', data),
  
  sendOTP: (data: { target: string; type: 'phone' | 'email' }) =>
    api.post('/api/auth/otp/send', data),
  
  verifyOTP: (data: { target: string; code: string; type: 'phone' | 'email' }) =>
    api.post('/api/auth/otp/verify', data),
};

// Exam APIs
export const examApi = {
  getExams: (params?: { status?: string; class_id?: number }) =>
    api.get('/api/exams', { params }),
  
  getExam: (id: number) =>
    api.get(`/api/exams/${id}`),
  
  startExam: (id: number) =>
    api.post(`/api/exams/${id}/start`),
  
  submitExam: (id: number, answers: { questionId: number; selectedOption?: string }[]) =>
    api.post(`/api/exams/${id}/submit`, { answers }),
  
  getResult: (id: number) =>
    api.get(`/api/exams/${id}/result`),
};

// User APIs
export const userApi = {
  getProfile: () =>
    api.get('/api/users/profile'),
  
  updateProfile: (data: { name?: string; classId?: number; avatar?: string }) =>
    api.put('/api/users/profile', data),
  
  getDashboard: () =>
    api.get('/api/users/dashboard'),
  
  getExamHistory: () =>
    api.get('/api/users/exam-history'),
  
  getBadges: () =>
    api.get('/api/users/badges'),
};

// Leaderboard APIs
export const leaderboardApi = {
  getLeaderboard: (params?: { period?: string; limit?: number }) =>
    api.get('/api/leaderboard', { params }),
};

// Class APIs
export const classApi = {
  getClasses: () =>
    api.get('/api/classes'),
  
  getSubjects: (classId: number) =>
    api.get(`/api/classes/${classId}/subjects`),
  
  getChapters: (subjectId: number) =>
    api.get(`/api/classes/subjects/${subjectId}/chapters`),
};

// CMS APIs
export const cmsApi = {
  getPage: (slug: string) =>
    api.get(`/api/cms/pages/${slug}`),
  
  getPages: () =>
    api.get('/api/cms/pages'),
  
  getSettings: () =>
    api.get('/api/cms/settings'),
  
  getSetting: (key: string) =>
    api.get(`/api/cms/settings/${key}`),
  
  getStats: () =>
    api.get('/api/cms/stats'),
  
  submitContact: (data: { name: string; email: string; phone?: string; subject?: string; message: string }) =>
    api.post('/api/cms/contact', data),
};

// Admin APIs
export const adminApi = {
  createExam: (data: object) =>
    api.post('/api/admin/exams', data),
  
  addQuestions: (examId: number, questions: object[]) =>
    api.post(`/api/admin/exams/${examId}/questions`, { questions }),
  
  getUsers: (params?: { page?: number; limit?: number }) =>
    api.get('/api/admin/users', { params }),
  
  getAnalytics: () =>
    api.get('/api/admin/analytics'),
  
  seedData: () =>
    api.post('/api/admin/seed'),
  
  // CMS Management
  createPage: (data: object) =>
    api.post('/api/cms/pages', data),
  
  updatePage: (id: number, data: object) =>
    api.put(`/api/cms/pages/${id}`, data),
  
  deletePage: (id: number) =>
    api.delete(`/api/cms/pages/${id}`),
  
  updateSetting: (key: string, value: string) =>
    api.put(`/api/cms/settings/${key}`, { value }),
  
  getContacts: () =>
    api.get('/api/cms/contacts'),
  
  updateContactStatus: (id: number, status: string) =>
    api.put(`/api/cms/contacts/${id}/status`, { status }),

  // Exam Management
  getExams: () =>
    api.get('/api/admin/exams'),

  getExam: (id: number) =>
    api.get(`/api/admin/exams/${id}`),

  updateExam: (id: number, data: object) =>
    api.put(`/api/admin/exams/${id}`, data),

  deleteExam: (id: number) =>
    api.delete(`/api/admin/exams/${id}`),

  updateQuestion: (examId: number, questionId: number, data: object) =>
    api.put(`/api/admin/exams/${examId}/questions/${questionId}`, data),

  deleteQuestion: (examId: number, questionId: number) =>
    api.delete(`/api/admin/exams/${examId}/questions/${questionId}`),
};
